import { Expose, Transform, Type } from 'class-transformer';

import { BaseRecord } from '../shared-models';
import { EDocumentType } from './file.model';
import { EFdvType } from './project.model';
import { UnitModel, UnitUserModel } from './unit.model';

export interface ICreateFDV {
  title?: string;
  parent_id?: number;
  filename_remote_url?: string;
  fileName?: string;
  fileSize?: number;
  fileExtension?: string;
  children: ICreateFDV[];
  unit_ids?: number[];
  unit_group_ids?: number[];
  unit_tag_ids?: number[];
  description?: string;
}

export interface IBatchCreateFDV extends ICreateFDV {
  folder_name?: string;
  position?: number;
  children: IBatchCreateFDV[];
}

export class FdvPeople {
  @Expose({ name: 'id' })
  id!: number;
  @Expose({ name: 'name' })
  name!: string;
  @Expose({ name: 'email' })
  email!: string;
}

export class ParentFolder {
  @Expose({ name: 'id' })
  id!: number;
  @Expose({ name: 'folder_name' })
  name!: string;
  @Expose({ name: 'title' })
  title?: string;
}

export enum FdvItemStatus {
  Approved = 'approved',
  Pending = 'pending',
  Declined = 'declined',
}

export class FdvBaseModel {
  @Expose({ name: 'id' })
  id!: number;
  @Expose({ name: 'title' })
  title!: string;
  @Expose({ name: 'folder_name' })
  name?: string;
  @Expose({ name: 'kind' })
  documentType!: EDocumentType;
  @Expose({ name: 'children' })
  @Type(() => FdvBaseModel)
  children!: FdvBaseModel[];
  @Expose({ name: 'parents' })
  @Type(() => ParentFolder)
  @Transform(({ value }) => {
    return (value as ParentFolder[])?.reverse() || [];
  })
  parents!: ParentFolder[];
  @Expose({ name: 'status' })
  status!: FdvItemStatus;

  active!: boolean;
  connected = false;
  selected = false;
  disabled = false;

  checkAndUpdateActiveState(): boolean {
    const hasActiveChild = this.children.some((child) => child.checkAndUpdateActiveState());
    if (hasActiveChild || this.active) {
      this.active = true;
      return true;
    }
    return false;
  }

  get parentId(): number | undefined {
    return this.parents?.[this.parents?.length - 1]?.id;
  }

  get hasPending(): boolean {
    return this.status === FdvItemStatus.Pending;
  }

  get hasApproved(): boolean {
    return this.status === FdvItemStatus.Approved;
  }
}

export class FdvIndexModel extends FdvBaseModel {
  @Expose({ name: 'created_at' })
  createdAt!: string;
  @Expose({ name: 'updated_at' })
  updatedAt!: string;
  @Expose({ name: 'position' })
  position!: number;
  @Expose({ name: 'folder_size' })
  size?: number;
  @Expose({ name: 'creator' })
  @Type(() => FdvPeople)
  creator!: FdvPeople;
  @Expose({ name: 'children' })
  @Type(() => FdvIndexModel)
  children!: FdvIndexModel[];
  @Expose({ name: 'parents' })
  @Type(() => ParentFolder)
  parents!: ParentFolder[];
  @Expose({ name: 'file_size' })
  fileSize?: number;
  @Expose({ name: 'file_extension' })
  fileExtension?: string;
  @Expose({ name: 'units' })
  units!: { id: number; identifier: string }[];
  @Expose({ name: 'unit_groups' })
  unitGroups!: { id: number; name: string; units: { id: number; identifier: string }[] }[];
  @Expose({ name: 'unit_tags' })
  unitTags!: BaseRecord[];
  @Expose({ name: 'pinned' })
  pinned!: boolean;
  @Expose({ name: 'description' })
  description?: string;

  active!: boolean;
}

export class FdvModel extends FdvIndexModel {
  @Expose({ name: 'updater' })
  @Type(() => FdvPeople)
  updater!: FdvPeople;
  @Expose({ name: 'children' })
  @Type(() => FdvModel)
  @Transform(({ value }) => {
    return (value as FdvModel[])?.sort((a, b) => (a.documentType === EDocumentType.Folder ? -1 : 1)) || [];
  })
  children!: FdvModel[];
  @Expose({ name: 'filename_remote_url' })
  filenameRemoteUrl?: string;
  @Expose({ name: 'filename' })
  filename?: {
    identifier: string;
    content_type: string;
    url: string;
    download_url: string;
  };
  @Expose({ name: 'description' })
  description!: string;

  get updaterName(): string {
    return this.creator.name;
  }

  get fileName() {
    return this.filename;
  }
}

export type AdvanceFdvModel = FdvModel | FdvBaseModel | FdvIndexModel;

export interface IUnitSelectFDV {
  unit: UnitUserModel | UnitModel;
  fdv: FdvModel[];
}

export interface UnitsFDV {
  [key: number]: IUnitSelectFDV;
}

export interface IMoveToRequest {
  folder_id: number | null;
  ids: number[];
}

export class FdvHistoryModel {
  projectId: number | null = null;
  unitId: number | null = null;
  parentId: number | null = null;
  documentId: number | null = null;
}

export class FdvAdvanceHistoryModel {
  projectId: number | null = null;
  parentId: number | null = null;
}

export class FdvConnectItemModel {
  @Expose({ name: 'connected_at' })
  connectedAt!: string | null;
  @Expose({ name: 'connected_by' })
  connectedBy!: string | null;
  @Expose({ name: 'folder_ids' })
  folderIds!: number[];
  @Expose({ name: 'folder_names' })
  folderNames!: string[];
  @Expose({ name: 'unit_id' })
  unitId!: number;
  @Expose({ name: 'unit_name' })
  unitName!: string;

  selected = false;
}

export class ProjectFdvSettingsModel {
  @Expose({ name: 'project_id' })
  projectId!: number;
  @Expose({ name: 'fdv_type' })
  fdvType!: EFdvType | null;
  @Expose({ name: 'fdv_needs_approve' })
  fdvNeedsApprove!: boolean;
  @Expose({ name: 'client_show_page_fdv' })
  clientShowPageFdv!: boolean;
  @Expose({ name: 'can_change_type' })
  canChangeType!: boolean;
  @Expose({ name: 'main_admin_approvers' })
  mainAdminApprovers!: { id: number; name: string; email: string }[];

  hasCurrentUserApprover = false;
}

export interface IFdvFileOverrides {
  filename: string;
  folderId: number;
  description: string;
  unitIds: number[];
  unitGroupIds: number[];
  tagIds: number[];
  remoteUrl?: string;
  title?: string;
}

class FdvQrDocumentModel {
  @Expose({ name: 'download_url' })
  downloadUrl!: string;
  @Expose({ name: 'url' })
  url!: string;
}

export class FdvQRCode {
  @Expose({ name: 'id' })
  id!: number;
  @Expose({ name: 'entity_id' })
  entityId!: number;
  @Expose({ name: 'entity_type' })
  entityType!: string;
  @Expose({ name: 'file_extension' })
  fileExtension!: string;
  @Expose({ name: 'file_size' })
  fileSize!: number;
  @Expose({ name: 'document' })
  @Type(() => FdvQrDocumentModel)
  document!: FdvQrDocumentModel;
}

export class FdvListResponse {
  @Expose({ name: 'fdvs' })
  @Type(() => FdvModel)
  fdvs!: FdvModel[];
  @Expose({ name: 'only_index' })
  onlyIndex!: boolean;
  @Expose({ name: 'files_count' })
  filesCount!: number;
  @Expose({ name: 'pinned_files_count' })
  pinnedFilesCount!: number;
}

export class FdvTemplateListModel {
  @Expose({ name: 'id' })
  id!: number;
  @Expose({ name: 'name' })
  name!: string;
  @Expose({ name: 'localized_name' })
  localizedName!: string;
}
